import { Dialog } from '@headlessui/react'
import { Form, Formik } from 'formik'
import type { FormikHelpers } from 'formik'
import { useEffect, useState } from 'react'

import SuccessIcon from '@Icons/success-icon.svg?react'
import XIcon from '@Icons/x-close-filter.svg?react'
import type { CreateProductDto } from '@Store/types/dto/product/createProductDto'
import {
  useEditProductDispatch,
  useEditProductState,
} from '@Utils/context/modal/useEditProduct'
import FormElement from '@Utils/forms/form-element'
import Currency from '@Utils/inputs/currency'
import BrandsDropdown from '@Utils/inputs/searchable-dropdown/brands'
import CategoriesDropdown from '@Utils/inputs/searchable-dropdown/categories'
import TagsDropdown from '@Utils/inputs/searchable-dropdown/tags'
import TextInput from '@Utils/inputs/text-input'
import type {
  CreateableDropdownOption,
  DropdownOption,
} from '@Utils/inputs/types'
import { editProductSchema } from '@Utils/schema/product/edit-product'

export default function EditProductModal(): JSX.Element {
  const {
    close,
    editProduct,
    handleClose: handleProductModalClose,
  } = useEditProductDispatch()
  const { product, isEditProductLoading, isEditProductSuccess } =
    useEditProductState()

  const [successEdit, setSuccessEdit] = useState<boolean>(false)
  const [productName, setProductName] = useState('')

  useEffect(() => {
    if (isEditProductSuccess === true) {
      setSuccessEdit(true)
    }
  }, [isEditProductSuccess])

  const [selectedBrand, setSelectedBrand] = useState<CreateableDropdownOption>({
    label: product?.brand?.name ?? '',
    value: `${product?.brand?.id}`,
  })

  const [selectedCategory, setSelectedCategory] =
    useState<CreateableDropdownOption>({
      label: product?.category?.name ?? '',
      value: `${product?.category?.id}`,
    })

  const [selectedTags, setSelectedTags] = useState<DropdownOption[]>(
    product?.product_tag.map((tag: any) => ({
      label: tag.tag.name,
      value: `${tag.tag.id}`,
    })) ?? [],
  )

  useEffect(() => {
    setSelectedBrand({
      label: product?.brand?.name ?? '',
      value: `${product?.brand?.id}`,
    })
    setSelectedCategory({
      label: product?.category?.name ?? '',
      value: `${product?.category?.id}`,
    })
    setSelectedTags(
      product?.product_tag.map((tag: any) => ({
        label: tag.tag.name,
        value: `${tag.tag.id}`,
      })) ?? [],
    )
  }, [product])

  useEffect(() => {
    handleProductModalClose(() => {
      setSuccessEdit(false)
      setSelectedBrand({
        label: product?.brand?.name ?? '',
        value: `${product?.brand?.id}`,
      })
      setSelectedCategory({
        label: product?.category?.name ?? '',
        value: `${product?.category?.id}`,
      })
      setSelectedTags(
        product?.product_tag.map((tag: any) => ({
          label: tag.tag.name,
          value: tag.tag.id,
        })) ?? [],
      )
      setProductName('')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product])

  const onSubmit = async (e: any) => {
    const createRequest: CreateProductDto = {
      new_product_name: e.new_product_name,
      product_barcode: e.product_barcode,
      product_code: e.product_code,
      product_cost: e.product_cost,
      additional_cost: e.additional_cost,
    }

    if (e.brand?.__isNew) {
      createRequest.brand_name = e.brand.value
    } else {
      createRequest.brand_id = e.brand
        ? parseInt(selectedBrand.value)
        : product?.brand?.id
    }

    if (e.category?.__isNew) {
      createRequest.category_name = e.category.value
    } else {
      createRequest.category_id = e.category
        ? parseInt(selectedCategory.value)
        : product?.category?.id
    }

    if (e.tags) {
      createRequest.product_tags = e.tags.map((tag: any) => {
        if (tag.__isNew) {
          return {
            name: tag.label.trim(),
          }
        } else {
          return {
            id: parseInt(tag.value),
          }
        }
      })
    }
    if (e.product_cost === '') {
      createRequest.product_cost = null
    }
    if ((e.additional_cost = '')) {
      createRequest.additional_cost = null
    }

    await editProduct({
      body: createRequest,
      id: product?.id,
    })
    setProductName(e.new_product_name)
  }
  const initialValues = {
    new_product_name: product?.name,
    category: product?.category?.id,
    brand: product?.brand?.id,
    product_cost: product?.cost,
    additional_cost: product?.additional_cost,
    product_code: product?.product_code,
    product_barcode: product?.barcode === null ? '' : product?.barcode,
    tags:
      product?.product_tag.map((tag: any) => ({
        label: tag.tag.name,
        value: tag.tag.id,
      })) ?? [],
  }

  const clearForm = (
    resetForm: FormikHelpers<any>['resetForm'],
    setFieldValue: FormikHelpers<any>['setFieldValue'],
    setFieldTouched: FormikHelpers<any>['setFieldTouched'],
  ) => {
    resetForm()
    setFieldValue('new_product_name', '')
    setFieldTouched('new_product_name', false)
    setFieldValue('product_code', '')
    setFieldTouched('product_code', false)
    setFieldValue('product_barcode', '')
    setFieldTouched('product_barcode', false)
    setFieldValue('product_cost', '')
    setFieldTouched('product_cost', false)
    setFieldValue('additional_cost', '')
    setFieldTouched('additional_cost', false)
    setSelectedBrand({
      value: '',
      label: 'Select...',
    })
    setFieldValue('brand', '')
    setFieldTouched('brand', false)
    setSelectedCategory({
      value: '',
      label: 'Select...',
    })
    setFieldValue('category', '')
    setFieldTouched('category', false)
    setSelectedTags([])
    setFieldTouched('tags', false)
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        background: 'rgba(41, 41, 41, 0.7)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <Dialog.Panel
        className={`relative border rounded-xl shadow-xl bg-white-100 ${
          !successEdit
            ? 'sm:w-2/3 w-full'
            : 'sm:w-2/3 md:w-1/2 lg:w-1/3 xl:w-1/5 w-full'
        } `}
      >
        {successEdit ? (
          <div className="flex flex-col justify-center items-center p-6">
            <button
              className="absolute right-6 top-6"
              onClick={() => {
                setSuccessEdit(false)
                close()
                setSelectedBrand({
                  label: product?.brand?.name ?? '',
                  value: `${product?.brand?.id}`,
                })
                setSelectedCategory({
                  label: product?.category?.name ?? '',
                  value: `${product?.category?.id}`,
                })
                setSelectedTags(
                  product?.product_tag.map((tag: any) => ({
                    label: tag.tag.name,
                    value: tag.tag.id,
                  })) ?? [],
                )
                setProductName('')
              }}
            >
              <XIcon />
            </button>
            <SuccessIcon />
            <p className="mb-2 mt-5">Product edited successfully</p>
            <p className="font-normal text-sm text-gray-500 mb-8 text-center">
              Product named “{productName}” has been edited. You can now see in
              your products.
            </p>
            <button
              className="bg-primary-600 active:bg-primary-700 border border-solid border-primary-600 rounded-lg shadow-sm w-full h-11"
              onClick={() => {
                setSuccessEdit(false)
                close()
                setSelectedBrand({
                  label: product?.brand?.name ?? '',
                  value: `${product?.brand?.id}`,
                })
                setSelectedCategory({
                  label: product?.category?.name ?? '',
                  value: `${product?.category?.id}`,
                })
                setSelectedTags(
                  product?.product_tag.map((tag: any) => ({
                    label: tag.tag.name,
                    value: tag.tag.id,
                  })) ?? [],
                )
                setProductName('')
              }}
            >
              <span className="font-medium text-base text-white-100">OK</span>
            </button>
          </div>
        ) : (
          <div className="flex flex-col p-6 ">
            <div className="flex flex-row justify-between items-center pb-5">
              <h1 className="font-medium text-lg text-gray-900">
                Edit Product
              </h1>
              <button
                onClick={() => {
                  close()
                  setSelectedBrand({
                    label: product?.brand?.name ?? '',
                    value: `${product?.brand?.id}`,
                  })
                  setSelectedCategory({
                    label: product?.category?.name ?? '',
                    value: `${product?.category?.id}`,
                  })
                  setSelectedTags(
                    product?.product_tag.map((tag: any) => ({
                      label: tag.tag.name,
                      value: tag.tag.id,
                    })) ?? [],
                  )
                  setProductName('')
                }}
              >
                <XIcon />
              </button>
            </div>
            <Formik
              onSubmit={onSubmit}
              initialValues={initialValues}
              validationSchema={editProductSchema}
            >
              {({
                resetForm,
                setFieldValue,
                setFieldTouched,
              }: FormikHelpers<any>) => (
                <Form className="w-full">
                  <div className="grid gap-6 md:grid-cols-2 font-medium text-sm text-gray-700 pb-8">
                    <FormElement label="Product Name" name="new_product_name">
                      <TextInput
                        name="new_product_name"
                        placeholder="Enter product name"
                      />
                    </FormElement>
                    <FormElement
                      label="Product Code"
                      name="product_code"
                      optional
                    >
                      <TextInput
                        name="product_code"
                        placeholder="Enter product code"
                      />
                    </FormElement>
                    <FormElement label="Category" name="category">
                      <CategoriesDropdown
                        name="category"
                        onChange={(category: DropdownOption) => {
                          setSelectedCategory(category)
                        }}
                        isCreatable={true}
                        value={selectedCategory}
                      />
                    </FormElement>
                    <FormElement label="Brand" name="brand">
                      <BrandsDropdown
                        name="brand"
                        onChange={(brand: DropdownOption) => {
                          setSelectedBrand(brand)
                        }}
                        isCreatable={true}
                        value={selectedBrand}
                      />
                    </FormElement>
                    <FormElement label="Tags" optional>
                      <TagsDropdown
                        name="tags"
                        onChange={(tag: DropdownOption[]) => {
                          setSelectedTags(tag)
                        }}
                        value={selectedTags}
                        isMulti
                      />
                    </FormElement>
                    <FormElement label="Barcode" optional>
                      <TextInput
                        name="product_barcode"
                        placeholder="Enter barcode number"
                      />
                    </FormElement>
                    <FormElement label="Product Cost" optional>
                      <Currency
                        name="product_cost"
                        placeholder="Enter product cost"
                      />
                    </FormElement>
                    <FormElement label="Additional Cost" optional>
                      <Currency
                        name="additional_cost"
                        placeholder="Enter additional cost"
                      />
                    </FormElement>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-between sm:space-x-3 space-y-3 sm:space-y-0">
                    <button
                      type="button"
                      onClick={() =>
                        clearForm(resetForm, setFieldValue, setFieldTouched)
                      }
                      className="w-full border border-solid py-2 font-normal text-base capitalize border-gray-300 active:bg-gray-100 shadow-sm rounded-lg h-11"
                    >
                      Clear
                    </button>
                    <button
                      type="submit"
                      disabled={
                        selectedCategory?.value === '' ||
                        selectedBrand?.value === '' ||
                        isEditProductLoading
                      }
                      className="w-full border border-solid border-primary-600 py-2 font-normal text-base capitalize text-white-100 shadow-sm rounded-lg bg-primary-600 h-11 disabled:opacity-75"
                    >
                      {isEditProductLoading ? 'Please wait...' : 'Save Changes'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </Dialog.Panel>
    </div>
  )
}
